import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  CardMedia,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  TextField,
  Typography,
  Paper,
  Alert,
  CircularProgress,
  Container
} from '@mui/material';
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon, DragIndicator, DeleteOutline } from '@mui/icons-material';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import axios from '../../utils/axiosConfig';
import { useNavigate, useLocation } from 'react-router-dom';

const TrainerManagement = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [trainers, setTrainers] = useState([]);
  const [open, setOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState('');
  const [editingTrainer, setEditingTrainer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    title: '',
    description: '',
    featured: false
  });
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [trainerToDelete, setTrainerToDelete] = useState(null);

  useEffect(() => {
    loadTrainers();
  }, []);

  const loadTrainers = async () => {
    try {
      setLoading(true);
      setError('');
      
      const response = await axios.get('/api/v2/trainers/admin');
      
      if (!response.data) {
        console.warn('No trainers data received');
        setTrainers([]);
        return;
      }

      if (Array.isArray(response.data)) {
        setTrainers(response.data);
      } else {
        console.error('Invalid trainer data format:', response.data);
        setError('Failed to load trainers: Invalid data format');
        setTrainers([]);
      }
    } catch (error) {
      console.error('Error loading trainers:', error);
      setError(error.response?.data?.message || 'Failed to load trainers. Please try again.');
      setTrainers([]);
      
      if (error.response?.status === 401) {
        navigate('/admin');
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchTrainers = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await axios.get('/api/v2/trainers');
      if (Array.isArray(response.data)) {
        setTrainers(response.data);
      } else {
        console.error('Invalid trainer data format:', response.data);
        setError('Failed to load trainers: Invalid data format');
        setTrainers([]);
      }
    } catch (error) {
      console.error('Error fetching trainers:', error);
      if (error.response?.status === 401) {
        navigate('/admin');
        return;
      }
      setError('Failed to load trainers. Please try again.');
      setTrainers([]);
    } finally {
      setLoading(false);
    }
  };

  const handleOpen = (trainer = null) => {
    if (trainer) {
      setEditingTrainer({
        id: trainer.id, // Ensure we store the ID
        ...trainer
      });
      setFormData({
        name: trainer.name,
        title: trainer.title,
        description: trainer.description,
        featured: trainer.featured || false
      });
      setPreviewUrl(trainer.imageUrl);
    } else {
      setEditingTrainer(null);
      setFormData({ name: '', title: '', description: '', featured: false });
      setPreviewUrl('');
      setSelectedFile(null);
    }
    setOpen(true);
    setError('');
  };

  const handleClose = () => {
    setOpen(false);
    setEditingTrainer(null);
    setFormData({ name: '', title: '', description: '', featured: false });
    setPreviewUrl('');
    setSelectedFile(null);
    setError('');
  };

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      if (file.size > 5000000) { // 5MB limit
        setError('Image size should be less than 5MB');
        return;
      }
      setSelectedFile(file);
      setPreviewUrl(URL.createObjectURL(file));
      setError('');
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      setError('');

      const formDataToSend = new FormData();
      formDataToSend.append('name', formData.name);
      formDataToSend.append('title', formData.title);
      formDataToSend.append('description', formData.description);
      formDataToSend.append('featured', formData.featured);
      
      if (selectedFile) {
        formDataToSend.append('image', selectedFile);
      }

      if (editingTrainer) {
        await axios.put(`/api/v2/trainers/${editingTrainer.id}`, formDataToSend, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        });
      } else {
        await axios.post('/api/v2/trainers', formDataToSend, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        });
      }

      setSuccessMessage(editingTrainer ? 'Trainer updated successfully!' : 'Trainer created successfully!');
      handleClose();
      loadTrainers();
    } catch (error) {
      console.error('Error saving trainer:', error);
      setError(error.response?.data?.message || 'Failed to save trainer. Please try again.');
      
      if (error.response?.status === 401) {
        navigate('/admin');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteClick = (trainer) => {
    setTrainerToDelete(trainer);
    setDeleteDialogOpen(true);
  };

  const handleDeleteCancel = () => {
    setTrainerToDelete(null);
    setDeleteDialogOpen(false);
  };

  const handleDelete = async () => {
    if (!trainerToDelete) return;

    try {
      setLoading(true);
      setError('');

      await axios.delete(`/api/v2/trainers/${trainerToDelete.id}`);

      setSuccessMessage('Trainer deleted successfully!');
      setDeleteDialogOpen(false);
      setTrainerToDelete(null);
      loadTrainers();
    } catch (error) {
      console.error('Error deleting trainer:', error);
      setError(error.response?.data?.message || 'Failed to delete trainer. Please try again.');
      
      if (error.response?.status === 401) {
        navigate('/admin');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleDragEnd = async (result) => {
    if (!result.destination) return;

    const items = Array.from(trainers);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    // Update order property for all items
    const updatedItems = items.map((item, index) => ({
      ...item,
      order: index
    }));

    // Optimistically update UI
    setTrainers(updatedItems);
    setError('');

    try {
      // Update order on server
      const response = await axios.put('/api/v2/trainers/reorder-trainers', {
        trainers: updatedItems.map((trainer) => ({
          id: trainer.id,
          order: trainer.order
        }))
      });

      // Update with server response to ensure consistency
      setTrainers(response.data);
      setSuccessMessage('Trainer order updated successfully');
    } catch (error) {
      console.error('Error updating trainer order:', error);
      // Revert to original order on error
      setTrainers(trainers);
      setError(error.response?.data?.message || 'Failed to update trainer order. Please try again.');
    }
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" component="h1">
            Manage Trainers
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Drag and drop trainers to reorder them. Changes are saved automatically.
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpen()}
          disabled={loading}
        >
          Add Trainer
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      {successMessage && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {successMessage}
        </Alert>
      )}

      {loading && !trainers.length ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
          <CircularProgress />
        </Box>
      ) : !trainers.length ? (
        <Typography variant="body1" color="text.secondary" align="center" sx={{ my: 4 }}>
          No trainers found. Click "Add Trainer" to get started.
        </Typography>
      ) : (
        <DragDropContext onDragEnd={handleDragEnd}>
          <Droppable droppableId="trainers">
            {(provided, snapshot) => (
              <Box
                {...provided.droppableProps}
                ref={provided.innerRef}
                sx={{ 
                  width: '100%',
                  transition: 'background-color 0.2s ease',
                  bgcolor: snapshot.isDraggingOver ? 'action.hover' : 'transparent',
                  borderRadius: 1,
                  p: 1
                }}
              >
                {trainers.map((trainer, index) => (
                  <Draggable
                    key={trainer.id}
                    draggableId={trainer.id}
                    index={index}
                  >
                    {(provided, snapshot) => (
                      <Card
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        sx={{
                          mb: 2,
                          display: 'flex',
                          transition: 'all 0.3s ease',
                          transform: snapshot.isDragging ? 'scale(1.02)' : 'none',
                          boxShadow: snapshot.isDragging ? 
                            '0 8px 16px -4px rgba(0,0,0,0.1), 0 4px 8px -4px rgba(0,0,0,0.06)' : 
                            '0 2px 4px -2px rgba(0,0,0,0.05), 0 1px 2px -1px rgba(0,0,0,0.03)',
                          bgcolor: snapshot.isDragging ? 'background.paper' : 'transparent',
                          ...(trainer.featured && {
                            border: '2px solid',
                            borderColor: 'primary.main',
                          }),
                          position: 'relative',
                          '&:hover .drag-handle': {
                            color: 'primary.main',
                            transform: 'scale(1.1)'
                          }
                        }}
                      >
                        <Box 
                          {...provided.dragHandleProps}
                          className="drag-handle"
                          sx={{ 
                            p: 2,
                            display: 'flex',
                            alignItems: 'center',
                            cursor: 'grab',
                            color: 'text.secondary',
                            transition: 'all 0.2s ease',
                            '&:hover': {
                              color: 'primary.main'
                            }
                          }}
                        >
                          <DragIndicator />
                        </Box>
                        <CardMedia
                          component="img"
                          sx={{ 
                            width: 151,
                            height: 151,
                            objectFit: 'cover'
                          }}
                          image={trainer.imageUrl || '/images/trainer-placeholder.jpg'}
                          alt={trainer.name}
                        />
                        <CardContent sx={{ flex: 1 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                            <Typography variant="h6" component="div">
                              {trainer.name}
                            </Typography>
                            {trainer.featured && (
                              <Typography 
                                variant="body2" 
                                color="primary"
                                sx={{ 
                                  ml: 2,
                                  fontWeight: 500,
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 0.5
                                }}
                              >
                                Featured Trainer
                              </Typography>
                            )}
                          </Box>
                          <Typography variant="subtitle1" color="text.secondary">
                            {trainer.title}
                          </Typography>
                          <Typography 
                            variant="body2" 
                            color="text.secondary" 
                            sx={{ 
                              mt: 1,
                              display: '-webkit-box',
                              WebkitLineClamp: 3,
                              WebkitBoxOrient: 'vertical',
                              overflow: 'hidden'
                            }}
                          >
                            {trainer.description}
                          </Typography>
                        </CardContent>
                        <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
                          <IconButton 
                            onClick={() => handleOpen(trainer)}
                            color="primary"
                            size="small"
                            sx={{
                              transition: 'transform 0.2s ease',
                              '&:hover': {
                                transform: 'scale(1.1)'
                              }
                            }}
                          >
                            <EditIcon />
                          </IconButton>
                          <IconButton
                            onClick={() => handleDeleteClick(trainer)}
                            color="error"
                            size="small"
                            disabled={loading}
                            sx={{
                              transition: 'transform 0.2s ease',
                              '&:hover': {
                                transform: 'scale(1.1)'
                              }
                            }}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Box>
                      </Card>
                    )}
                  </Draggable>
                ))}
                {provided.placeholder}
              </Box>
            )}
          </Droppable>
        </DragDropContext>
      )}
      <Dialog 
        open={open} 
        onClose={handleClose} 
        maxWidth="sm" 
        fullWidth
      >
        <DialogTitle>
          {editingTrainer ? 'Edit Trainer' : 'Add New Trainer'}
        </DialogTitle>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          <Box component="form" onSubmit={handleSubmit} sx={{ mt: 2 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField
                  required
                  fullWidth
                  label="Name"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  disabled={loading}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  required
                  fullWidth
                  label="Title"
                  name="title"
                  value={formData.title}
                  onChange={handleInputChange}
                  disabled={loading}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  required
                  fullWidth
                  multiline
                  rows={4}
                  label="Description"
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  disabled={loading}
                />
              </Grid>
              <Grid item xs={12}>
                <input
                  accept="image/*"
                  style={{ display: 'none' }}
                  id="trainer-image"
                  type="file"
                  onChange={handleFileSelect}
                  disabled={loading}
                />
                <label htmlFor="trainer-image">
                  <Button 
                    variant="outlined" 
                    component="span"
                    disabled={loading}
                  >
                    {editingTrainer ? 'Change Image' : 'Upload Image'}
                  </Button>
                </label>
                {previewUrl && (
                  <Box sx={{ mt: 2 }}>
                    <img
                      src={previewUrl}
                      alt="Preview"
                      style={{ maxWidth: '100%', maxHeight: 200, borderRadius: 8 }}
                    />
                  </Box>
                )}
              </Grid>
              <Grid item xs={12}>
                <Box sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
                  <Button
                    variant={formData.featured ? "contained" : "outlined"}
                    onClick={() => setFormData(prev => ({ ...prev, featured: !prev.featured }))}
                    sx={{ mr: 1 }}
                  >
                    {formData.featured ? "Featured Trainer" : "Set as Featured"}
                  </Button>
                  <Typography variant="body2" color="text.secondary">
                    Featured trainers appear more prominently on the home page
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={loading || (!selectedFile && !editingTrainer)}
          >
            {loading ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={deleteDialogOpen}
        onClose={handleDeleteCancel}
        PaperProps={{
          sx: {
            borderRadius: 2,
            maxWidth: '400px'
          }
        }}
      >
        <DialogTitle sx={{ 
          pb: 1,
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          color: 'error.main'
        }}>
          <DeleteOutline /> Delete Trainer
        </DialogTitle>
        <DialogContent sx={{ pb: 1 }}>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Are you sure you want to delete{' '}
            <strong>{trainerToDelete?.name}</strong>? This action cannot be undone.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            All associated data will be permanently removed from the system.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={handleDeleteCancel}
            variant="outlined"
            sx={{ 
              borderWidth: 2,
              '&:hover': { borderWidth: 2 }
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleDelete}
            variant="contained"
            color="error"
            disabled={loading}
            sx={{ 
              ml: 2,
              px: 3,
              '&:hover': {
                bgcolor: 'error.dark'
              }
            }}
          >
            {loading ? <CircularProgress size={24} color="inherit" /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default TrainerManagement;
